<template>
  <MainHeader
    v-if="!isShowCart"
    :currentType="currentCapsuleType"
    :catalogData="catalogData"
    @type-change="handleCapsuleTypeChange"
    @product-search="handleProductSearch"
  />

  <div v-if="!isShowCart" class="discount-info">
    <p>При заказе от 10 упаковок - скидка {{ currentCapsuleType === 'original' ? '25' : '50' }}₽ с каждой упаковки!</p>
  </div>

  <div v-if="!isShowCart" class="catalog-container">
    <h1 class="catalog-heading">{{ catalogHeading }}</h1>

    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Загрузка каталога...</p>
    </div>

    <div v-else-if="apiError && filteredCollections.length === 0" class="empty-state">
      <h3>Ошибка загрузки</h3>
      <p>{{ apiError }}</p>
    </div>

    <div v-else-if="filteredCollections.length === 0" class="empty-state">
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="#876C43"/>
      </svg>
      <h3>Товары не найдены</h3>
      <p>Нет доступных товаров в этой категории</p>
    </div>

    <div v-else class="catalog">
      <div v-for="collection in filteredCollections" :key="collection.id" class="collection-section">
        <div class="capsule-list">
          <CollectionCard
            :name="collection.name"
            :description="collection.description"
            :size="collection.size"
          />

          <CoffeeCard
            v-for="item in collection.items"
            :key="item.id || item.name"
            :data-product-id="item.id"
            :coffee="item"
            :quantity="getQuantity(item.name)"
            :category="item.size || collection.size"
            @update-quantity="updateFromCart"
          />
        </div>
      </div>
    </div>
  </div>

  <div v-if="isShowCart" class="cart-view">
    <CoffeeCart :cart="cart" @edit-cart="handleOpenShowCase" @send-cart="handleSendCart" @update-quantity="updateFromCart"></CoffeeCart>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import MainHeader from './components/MainHeader.vue'
import CoffeeCard from './components/CoffeeCard.vue'
import CoffeeCart from './components/CoffeeCart.vue'
import CollectionCard from './components/CollectionCard.vue'
import staticCatalog from './data/catalog.js'

export default {
  name: 'NespressoShop',
  components: {
    MainHeader,
    CoffeeCard,
    CoffeeCart,
    CollectionCard
  },
  setup() {
    const cart = ref([]);
    // Static, bundled catalog — no backend fetch at runtime.
    const catalogData = reactive({
      original: staticCatalog.original,
      vertuo: staticCatalog.vertuo
    });
    let isShowCart = ref(false);
    let isLoading = ref(false);
    let apiError = ref(null);
    let currentCapsuleType = ref('original');

    // Computed catalog heading
    const catalogHeading = computed(() => {
      return currentCapsuleType.value === 'original'
        ? 'Nespresso ORIGINAL'
        : 'Nespresso VERTUO';
    });

    // Live cart totals surfaced in the native Telegram main button.
    const cartCount = computed(() => cart.value.reduce((n, ci) => n + ci.quantity, 0));
    const cartSubtotal = computed(() =>
      cart.value.reduce((sum, ci) => sum + ci.item.price * ci.quantity, 0)
    );

    // While browsing the catalog, show "🛒 N шт · SUM ₽" in the main button so
    // the count and subtotal are always visible. In the cart view the CoffeeCart
    // component owns the button (its text becomes "Отправить заказ"), so skip.
    function updateMainButton() {
      const wa = window.Telegram?.WebApp;
      if (!wa?.MainButton || isShowCart.value) return;
      const text = cartCount.value > 0
        ? `🛒 ${cartCount.value} шт · ${cartSubtotal.value.toLocaleString('ru-RU')} ₽`
        : 'Посмотреть заказ';
      wa.MainButton.setParams({ text, isVisible: true });
    }

    watch([cartCount, cartSubtotal, isShowCart], updateMainButton);

    onMounted(() => {
      if (window.Telegram.WebApp) {
        window.Telegram.WebApp.MainButton.show();
        window.Telegram.WebApp.onEvent('mainButtonClicked', handleOpenCart);
        updateMainButton();
      } else {
        console.error("Telegram WebApp script not loaded.");
      }
    });

    // Computed property to filter collections by capsule type
    const filteredCollections = computed(() => {
      const collections = catalogData[currentCapsuleType.value] || [];

      // Only show collections that have items with stock
      return collections.filter(collection => {
        if (!collection.items || collection.items.length === 0) {
          return false;
        }

        // Check if any items have stock
        const hasAvailableItems = collection.items.some(item => {
          const hasStockProp = Object.prototype.hasOwnProperty.call(item, 'inStock');
          return !hasStockProp || item.inStock === undefined || item.inStock > 0;
        });

        return hasAvailableItems;
      }).map(collection => ({
        ...collection,
        items: collection.items.filter(item => {
          const hasStockProp = Object.prototype.hasOwnProperty.call(item, 'inStock');
          return !hasStockProp || item.inStock === undefined || item.inStock > 0;
        })
      }));
    });

    // Handle capsule type change
    function handleCapsuleTypeChange(newType) {
      currentCapsuleType.value = newType;
    }

    // Handle product search result click
    function handleProductSearch(product) {
      // Switch to the correct catalog type
      if (product.catalogType !== currentCapsuleType.value) {
        currentCapsuleType.value = product.catalogType;
      }

      // Scroll to the product after a short delay to allow catalog to update
      setTimeout(() => {
        scrollToProduct(product.id);
      }, 100);
    }

    // Scroll to product by ID
    function scrollToProduct(productId) {
      const productElement = document.querySelector(`[data-product-id="${productId}"]`);
      if (productElement) {
        const headerHeight = 200; // Approximate header height
        const elementPosition = productElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });

        // Add highlight effect
        productElement.classList.add('highlight-product');
        setTimeout(() => {
          productElement.classList.remove('highlight-product');
        }, 2000);
      }
    }

    function addToCart(item) {
      const existingItem = cart.value.find(cartItem => cartItem.item.name === item.name);
      if (existingItem) {
        existingItem.quantity++;
      } else {
        cart.value.push({ item: item, quantity: 1 });
      }
    }

    function updateFromCart({ coffee, change }) {
      const itemIndex = cart.value.findIndex(cartItem => cartItem.item.name === coffee.name);
      if (itemIndex !== -1) {
        cart.value[itemIndex].quantity += change;
        if (cart.value[itemIndex].quantity <= 0) {
          cart.value.splice(itemIndex, 1);
        }
      } else if (change > 0) {
        addToCart(coffee);
      }
    }

    // Auto-navigate back to catalog when cart becomes empty
    watch(() => cart.value.length, (newLen) => {
      if (newLen === 0 && isShowCart.value) {
        handleOpenShowCase();
      }
    });

    function getQuantity(name) {
      const item = cart.value.find(cartItem => cartItem.item.name === name);
      return item ? item.quantity : 0;
    }

    function handleOpenCart() {
      isShowCart.value = true;

      window.Telegram.WebApp.onEvent('backButtonClicked', handleOpenShowCase);
      window.Telegram.WebApp.offEvent('mainButtonClicked', handleOpenCart);
    }

    function handleOpenShowCase() {
      isShowCart.value = false;

      window.Telegram.WebApp.BackButton.hide();
      window.Telegram.WebApp.onEvent('mainButtonClicked', handleOpenCart);
      updateMainButton();
    }

    function toggleCart() {
      if (isShowCart.value) {
        handleOpenShowCase();
      } else {
        handleOpenCart();
      }
    }

    function handleSendCart(cartSummary) {
      console.log('Sending cart data...');
      const cartForSending = cart.value.map(item => ({
        name: item.item.name,
        quantity: item.quantity,
        price: item.item.price
      }));

      const dataToSend = JSON.stringify({cart: cartForSending, summary: cartSummary});
      console.log('Data to send:', dataToSend);

      // Send data to Telegram bot
      window.Telegram.WebApp.sendData(dataToSend);

      // Clear cart
      cart.value = [];

      // Close the web app
      console.log('Closing app...');
      window.Telegram.WebApp.close();
    }

    return {
      catalogData,
      filteredCollections,
      catalogHeading,
      currentCapsuleType,
      cart,
      isShowCart,
      isLoading,
      apiError,
      addToCart,
      updateFromCart,
      getQuantity,
      toggleCart,
      handleOpenShowCase,
      handleSendCart,
      handleCapsuleTypeChange,
      handleProductSearch
    };
  }
}
</script>

<style>
* {
  box-sizing: border-box;
}

body {
  padding: 0;
  margin: 0;
  background-color: #FAF9F8;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.discount-info {
  margin: 0;
  padding: 15px;
  background: linear-gradient(135deg, #876C43 0%, #A08060 100%);
  color: white;
  font-size: 16px;
  text-align: center;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.catalog-container {
  width: 100%;
}

.catalog-heading {
  text-align: center;
  margin: 30px 20px 20px 20px;
  color: #876C43;
  font-size: 28px;
  font-weight: 600;
}

.catalog {
  display: flex;
  flex-direction: column;
  align-items: left;
  gap: 20px;
  margin-bottom: 2rem;
  padding: 0 20px;
}

.collection-section {
  width: 100%;
}

.capsule-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #876C43;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #E5D5BB;
  border-top-color: #876C43;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-container p {
  font-size: 16px;
  font-weight: 500;
  margin: 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  background-color: #FAF9F8;
  border-radius: 8px;
  max-width: 600px;
  margin: 2rem auto;
  gap: 1rem;
}

.empty-state h3 {
  color: #876C43;
  margin: 0;
  font-size: 20px;
}

.empty-state p {
  color: #666;
  font-size: 16px;
  margin: 0;
  text-align: center;
}

@media (max-width: 768px) {
  .capsule-list {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .catalog-heading {
    margin-left: 10px;
    font-size: 1.5rem;
  }

  .catalog {
    padding: 0 10px;
  }
}

@media (max-width: 480px) {
  .capsule-list {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Highlight effect for search results */
.highlight-product {
  animation: highlight-pulse 2s ease-in-out;
  box-shadow: 0 0 20px rgba(135, 108, 67, 0.6) !important;
  transform: scale(1.05);
  z-index: 10;
  position: relative;
  transition: transform 0.3s ease-out, box-shadow 0.3s ease-out;
}

@keyframes highlight-pulse {
  0% {
    box-shadow: 0 0 20px rgba(135, 108, 67, 0.6);
    transform: scale(1.05);
  }
  50% {
    box-shadow: 0 0 30px rgba(135, 108, 67, 0.8);
    transform: scale(1.08);
  }
  100% {
    box-shadow: 0 0 20px rgba(135, 108, 67, 0.6);
    transform: scale(1.05);
  }
}

.cart-view {
  min-height: 100vh;
  background-color: #FAF9F8;
  padding: 20px;
}

@media (max-width: 768px) {
  .cart-view {
    padding: 15px;
  }
}
</style>
